import classNames from "classnames";
import { Button } from "components/DesignSystem/Button/Button";
import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import { DateInput } from "components/DesignSystem/DateInput/DateInput";
import { Link } from "components/DesignSystem/Link/Link";
import Loader from "components/DesignSystem/Loader/loader";
import Modal from "components/DesignSystem/Modal/Modal";
import { ADHOC_SEASON_MIN_YEAR } from "constants/adhocTaskIdentifier";
import { INKLE_WEBSITE_URL } from "constants/base";
import { TextInput } from "components/DesignSystem/TextInput/TextInput";
import { InfoSolid } from "components/InfoSolid";
import { NumericStepper } from "components/NumericStepper/NumericStepper";
import { PriceInput } from "components/PriceInput/PriceInput";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import ConditionalToolTip from "components/design/conditionalToolTip";
import ToolTip from "components/design/toolTip";
import { Cart } from "components/icons/Cart";
import {
  ACCOUNTING_METHOD_TYPES,
  ACCOUNTING_METHODS,
  ACCOUNTING_METHODS_OPTIONS,
} from "constants/bookkeeping";
import { YYYY_MM_DD } from "constants/date";
import dayjs from "dayjs";
import { addProductToCartValidation } from "formValidations/addProductToCartValidation";
import { Field, FieldProps, Form, Formik } from "formik";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useToast } from "hooks/useToast";
import ReactCountryFlag from "react-country-flag";
import { useParams } from "react-router-dom";
import { MultiValue, SingleValue } from "react-select";
import {
  useAddProductToCartMutation,
  useGetAllProductsQuery,
  Product,
  useGetDeletedCartItemQuery,
  AddToCartPayload,
  QuarterlyAdvanceTaxMetadata,
  BundleToMetadata,
  YearlyAdvanceTaxMetadata,
  FilingState,
} from "store/apis/products";
import { useLazyGetAllTransactionsDataQuery } from "store/apis/transactions";
import {
  AD_HOC,
  FOREIGN_QUALIFICATION,
  QUARTERLY_ADVANCE_TAX,
  SALES_TAX_REGISTRATION,
  STATE_FILING,
  STATE_REGISTRATION_PAYROLL,
  STATE_ANNUAL_REPORTS,
  STATE_WITHDRAWAL,
  US_BOOKKEEPING_ONE_TIME,
  YEARLY_ADVANCE_TAX,
} from "types/Models/services";
import { BackendError } from "types/utils/error";
import { getSeasonsFrom } from "utils/getAllSeason";
import { CalculatedAmountForAnnualCatchup } from "./CalculatedAmountForAnnualCatchup";
import { ConditionalAddProductToCartWrapper } from "./ConditionalAddProductToCartWrapper";
import { Banner } from "components/DesignSystem/Banner/Banner";
import { CART_ITEM_DELETION_REASON_DICT } from "constants/cartItemDeletionReasonsDict";
import { CART_ITEM_STATUS } from "constants/cart";
import { AdvanceTaxDeadline } from "./AdvanceTaxDeadline";
import { AdvanceQuarterlyTaxPrice } from "./AdvanceQuarterlyTaxPrice";
import { PricingTierSelector } from "./PricingTierSelector";
import { useQuery } from "hooks/useQuery";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import { DateField } from "components/DesignSystem/DateInput/DateField";
import { CalendarIcon } from "components/icons/CalendarIcon";
import { AddBulkCartItem, AddCartItem } from "constants/BulkCartAction";
import { Info } from "components/icons/Info";
import {
  FOREIGN_QUALIFICATION_PRICING_TIER_STANDARD,
  FOREIGN_QUALIFICATION_PRICING_TIER_PRIORITY,
  FOREIGN_QUALIFICATION_PRICING_TIER_OPTIONS,
  FOREIGN_QUALIFICATION_PRICING_TIER_TURNAROUND,
  ForeignQualificationPricingTier,
} from "constants/foreignQualificationPricingTier";
import {
  PAYROLL_REGISTRATION_PRICING_TIER_STANDARD,
  PAYROLL_REGISTRATION_PRICING_TIER_PRIORITY,
  PAYROLL_REGISTRATION_PRICING_TIER_OPTIONS,
  PAYROLL_REGISTRATION_PRICING_TIER_TURNAROUND,
  PAYROLL_REGISTRATION_FALLBACK_PRICE,
  PayrollRegistrationPricingTier,
} from "constants/payrollRegistrationPricingTier";

type PricingTier =
  | ForeignQualificationPricingTier
  | PayrollRegistrationPricingTier;

const getPriceForTier = (state: FilingState | undefined, tier: PricingTier) => {
  // FQ and Payroll priority tiers share the same underlying "PRIORITY" value
  if (tier === FOREIGN_QUALIFICATION_PRICING_TIER_PRIORITY) {
    return state?.priority_price;
  }

  return state?.price;
};

export const EntityLabel = ({
  name,
  countryCode,
}: {
  name: string;
  countryCode: string;
}) => (
  <div className="t-flex t-gap-1.5 t-items-center">
    <span className="t-rounded-sm">
      <ReactCountryFlag svg countryCode={countryCode} />
    </span>{" "}
    <span>{name}</span>
  </div>
);

const DeletedCartItemInfo = (props: AddToCartPayload) => {
  const entityId = useCurrentEntityId();

  let validated = false;

  try {
    addProductToCartValidation({}).validateSync(props);
    validated = true;
  } catch (error) {}

  const { data: deletedItem } = useGetDeletedCartItemQuery(
    {
      payload: props,
      entityId,
    },
    {
      skip: !entityId || !validated,
    }
  );

  if (
    deletedItem?.delete_reason &&
    deletedItem?.status === CART_ITEM_STATUS.ARCHIVED
  ) {
    return (
      <Banner
        type="error"
        description={
          <span className="t-text-red-70">
            Customer has marked this filing as{" "}
            {CART_ITEM_DELETION_REASON_DICT[deletedItem.delete_reason]}.
          </span>
        }
      />
    );
  }

  return null;
};

export const AddProductToCart = ({
  onClose,
  productIdFromProps,
  currentSeason,
  entityIdFromProps,
  onAdd,
  useOwnModal,
  setCurrentStep,
  ignoreCountErrors = false,
  hideEntity,
  handleOwnAdd,
  initialValue,
  groupIdFromProps,
  submitBtnText,
}: {
  onClose: () => void;
  productIdFromProps?: string;
  currentSeason?: number;
  entityIdFromProps?: string;
  onAdd?: () => void;
  useOwnModal?: boolean;
  setCurrentStep?: (step: number) => void;
  ignoreCountErrors?: boolean;
  hideEntity?: boolean;
  handleOwnAdd?: (cartItem: AddCartItem) => void;
  initialValue?: AddBulkCartItem;
  groupIdFromProps?: string;
  submitBtnText?: string;
}) => {
  const { successToast, alertToast } = useToast();
  const group = useCurrentGroupContext();
  const groupId = groupIdFromProps || group.uuid;
  const entityUuid = useCurrentEntityId();
  const entityId = entityIdFromProps || entityUuid;
  const { productId: productUuid } = useParams<{ productId?: string }>();
  const { isCustomer, isAdmin } = useRoleBasedView();
  const productId = productIdFromProps || productUuid;
  const query = useQuery();
  const seasonFromQuery = query.get("season");

  const { data: products, isLoading } = useGetAllProductsQuery(
    { groupId, entityId },
    { skip: !groupId || !entityId }
  );
  const [addProductToCart, { isLoading: addingProductToCart }] =
    useAddProductToCartMutation();

  const [getTransactions, { isFetching: gettingTransactions }] =
    useLazyGetAllTransactionsDataQuery();

  const calculateBookkeepingPrice = async ({
    startDate,
    endDate,
    setPrice,
    accountingMethod,
  }: {
    startDate: string;
    endDate: string;
    setPrice: (price: number) => void;
    accountingMethod: ACCOUNTING_METHOD_TYPES;
  }) => {
    const payload = {
      groupId,
      entityId,
      pageSize: "1",
      page_num: 1,
      startDate: { value: startDate },
      endDate: { value: dayjs(endDate).endOf("month").format(YYYY_MM_DD) },
      txnAccountType: "EXCLUDE_STRIPE" as const,
    };

    const transactions = await getTransactions(payload).unwrap();

    if (
      transactions.total_count <= 199 &&
      accountingMethod === ACCOUNTING_METHODS.CASH
    ) {
      return setPrice(199);
    }

    if (
      transactions.total_count <= 199 &&
      accountingMethod === ACCOUNTING_METHODS.ACCRUAL
    ) {
      return setPrice(399);
    }

    if (accountingMethod === ACCOUNTING_METHODS.ACCRUAL) {
      return setPrice(transactions.total_count * 2);
    }

    if (accountingMethod === ACCOUNTING_METHODS.CASH) {
      setPrice(transactions.total_count);
    }
  };

  if (useOwnModal && isLoading) {
    return (
      <Modal.Body className="t-flex t-h-[482px] t-w-full t-items-center t-justify-center">
        <Loader size="small" />
      </Modal.Body>
    );
  }

  if (isLoading) {
    return (
      <Modal.Root open>
        <Modal.Content>
          <Modal.Header>
            <Modal.Title>Add to cart</Modal.Title>
            <Modal.Close />
          </Modal.Header>
          <Modal.Body>
            <div className="t-flex t-h-[482px] t-w-full t-items-center t-justify-center">
              <Loader size="small" />
            </div>
          </Modal.Body>
        </Modal.Content>
      </Modal.Root>
    );
  }

  if (!products) {
    return null;
  }

  const allProducts = Object.values(products).flatMap((productGroup) => {
    return Object.values(productGroup).flatMap((value) => {
      return Array.isArray(value) && value.length > 0
        ? (value.filter((item) => !("subscription_name" in item)) as Product[])
        : [];
    });
  });

  const selectedProduct = allProducts.find(
    (p) => p.uuid === productId
  ) as Product;

  const {
    title,
    product_content_type_id,
    uuid,
    seasons,
    quantity_type,
    approx_deadline,
    approx_price,
    description,
    states,
    has_from_date,
    has_accounting_method,
    has_to_date,
    base_task_key,
    quarters,
    metadata,
  } = selectedProduct || {};
  const isAdhoc = base_task_key === AD_HOC;
  const nextYear = dayjs().year() + 1;
  const allSeasons = isAdhoc
    ? getSeasonsFrom(ADHOC_SEASON_MIN_YEAR, nextYear)
    : seasons;
  const isCustomDeadline =
    base_task_key === QUARTERLY_ADVANCE_TAX ||
    base_task_key === YEARLY_ADVANCE_TAX;
  const currentYearSeason = allSeasons?.find((s) => s === dayjs().year());
  const defaultSeason = seasonFromQuery
    ? allSeasons?.find((s) => s === Number(seasonFromQuery))
    : currentYearSeason;
  const selectedYear = dayjs().year().toString();

  const onSubmit = async ({
    entity_id,
    season,
    quantity,
    state_id,
    payment_amount,
    from_date,
    to_date,
    accounting_method,
    task_name,
    quarter,
    bundle,
    deadline,
  }: {
    entity_id: string;
    season?: number;
    quantity: number;
    state_id?: string;
    payment_amount?: number;
    from_date?: string | null;
    to_date?: string | null;
    accounting_method?: string;
    task_name?: string;
    quarter?: string;
    bundle?: boolean;
    deadline: string;
  }) => {
    const taskName = task_name ? { task_name } : {};
    const bundleMetadata =
      metadata?.hasOwnProperty("fields") && "fields" in metadata
        ? metadata.fields.find(
            (data) => (data as BundleToMetadata).type === "bundle_checkbox"
          )
        : null;
    const bundleTo = bundle
      ? {
          parent_template: (bundleMetadata as BundleToMetadata)
            ?.base_task_template_id,
        }
      : {};
    const deadlineDate = deadline
      ? { deadline_date: dayjs(deadline).format(YYYY_MM_DD) }
      : {};
    if (handleOwnAdd) {
      handleOwnAdd({
        entity_id,
        season,
        quantity,
        state_id,
        payment_amount,
        from_date: from_date ? dayjs(from_date).format(YYYY_MM_DD) : undefined,
        to_date: to_date
          ? dayjs(to_date).endOf("month").format(YYYY_MM_DD)
          : undefined,
        accounting_method,
        ...deadlineDate,
        ...taskName,
        ...bundleTo,
        quarter,
        product_content_type_id,
        product_id: uuid,
        base_task_key,
      });
      return;
    }
    if (groupId && uuid && product_content_type_id) {
      try {
        const payload = {
          entity_id,
          season: season ? String(season) : null,
          product_content_type_id,
          product_id: uuid,
          quantity: quantity,
          payment_amount,
          state_id,
          from_date: from_date
            ? dayjs(from_date).format(YYYY_MM_DD)
            : undefined,
          to_date: to_date
            ? dayjs(to_date).endOf("month").format(YYYY_MM_DD)
            : undefined,
          accounting_method,
          quarter,
          ...deadlineDate,
          ...taskName,
          ...bundleTo,
        };

        await addProductToCart({
          groupId,
          entityId,
          payload: payload,
        }).unwrap();

        successToast({ title: "Item added!" });
        onClose();
        onAdd?.();
      } catch (e) {
        const error = e as BackendError;

        if (
          ignoreCountErrors &&
          error.data?.error?.code === "cart_item_already_exists"
        ) {
          return onAdd?.();
        }

        alertToast({ message: error.data?.error?.message });
      }
    }
  };

  const useApproxPriceAsPaymentAmount =
    base_task_key === STATE_WITHDRAWAL ||
    base_task_key === STATE_REGISTRATION_PAYROLL ||
    base_task_key === STATE_ANNUAL_REPORTS ||
    base_task_key === FOREIGN_QUALIFICATION ||
    base_task_key === SALES_TAX_REGISTRATION;

  const isForeignQualification = base_task_key === FOREIGN_QUALIFICATION;
  const isPayrollRegistration = base_task_key === STATE_REGISTRATION_PAYROLL;
  const showPricingTier = isForeignQualification || isPayrollRegistration;

  const pricingTierConfig = isPayrollRegistration
    ? {
        standardTierValue:
          PAYROLL_REGISTRATION_PRICING_TIER_STANDARD as PricingTier,
        priorityTierValue:
          PAYROLL_REGISTRATION_PRICING_TIER_PRIORITY as PricingTier,
        options: PAYROLL_REGISTRATION_PRICING_TIER_OPTIONS,
        turnaround: PAYROLL_REGISTRATION_PRICING_TIER_TURNAROUND,
        fallbackPrice: PAYROLL_REGISTRATION_FALLBACK_PRICE as
          | number
          | undefined,
      }
    : {
        standardTierValue:
          FOREIGN_QUALIFICATION_PRICING_TIER_STANDARD as PricingTier,
        priorityTierValue:
          FOREIGN_QUALIFICATION_PRICING_TIER_PRIORITY as PricingTier,
        options: FOREIGN_QUALIFICATION_PRICING_TIER_OPTIONS,
        turnaround: FOREIGN_QUALIFICATION_PRICING_TIER_TURNAROUND,
        fallbackPrice: undefined as number | undefined,
      };

  const resolvePaymentAmountForTier = (
    state: FilingState | undefined,
    tier: PricingTier
  ): number | string | undefined => {
    const statePrice = getPriceForTier(state, tier);
    if (statePrice != null) {
      return Number(statePrice);
    }

    if (pricingTierConfig.fallbackPrice != null) {
      return pricingTierConfig.fallbackPrice;
    }

    if (showPricingTier && isNaN(Number(approx_price))) {
      return "";
    }

    return undefined;
  };

  const hasInitialPaymentAmount = initialValue?.payment_amount != null;
  let initialPaymentAmount = initialValue?.payment_amount ?? 0;
  if (useApproxPriceAsPaymentAmount && approx_price) {
    initialPaymentAmount = Number(approx_price);
  }
  if (!hasInitialPaymentAmount && pricingTierConfig.fallbackPrice != null) {
    initialPaymentAmount = pricingTierConfig.fallbackPrice;
  }

  return (
    <ConditionalAddProductToCartWrapper
      useOwnModal={useOwnModal}
      title={title}
      onClose={onClose}
    >
      <Formik
        initialValues={{
          entity_id: entityId,
          product_id: uuid,
          product_content_type_id:
            initialValue?.product_content_type_id || product_content_type_id,
          season: initialValue?.season || currentSeason || defaultSeason,
          has_season: Boolean(seasons && seasons.length > 0),
          has_states: Boolean(states && states.length > 0),
          has_accounting_method: has_accounting_method,
          has_from_date: has_from_date,
          quantity: initialValue?.quantity ?? 1,
          state_id: initialValue?.state_id || "",
          pricing_tier: showPricingTier
            ? pricingTierConfig.standardTierValue
            : "",
          payment_amount: initialPaymentAmount,
          from_date: initialValue?.from_date || "",
          to_date: initialValue?.to_date || "",
          accounting_method:
            initialValue?.accounting_method ||
            (has_accounting_method ? "CASH" : ""),
          task_name: initialValue?.task_name || "",
          quarter: initialValue?.quarter || "",
          bundle: initialValue?.bundle ?? false,
          deadline: initialValue?.deadline_date || "",
        }}
        validationSchema={addProductToCartValidation({ hideEntity })}
        validationContext={{
          needDeadline:
            base_task_key === STATE_FILING || base_task_key === AD_HOC,
        }}
        onSubmit={onSubmit}
      >
        {({ submitForm, values, setFieldValue, touched, isValid, errors }) => {
          const selectedEntity = group?.entities?.find(
            (e) => e.uuid === values.entity_id
          );

          const setPrice = (price: number) => {
            setFieldValue("payment_amount", price);
          };

          const onAccountingMethodChange = (
            option: MultiValue<OptionData> | SingleValue<OptionData>
          ) => {
            if (touched.payment_amount) {
              return;
            }

            if (option instanceof Array) {
              return;
            }

            if (base_task_key === US_BOOKKEEPING_ONE_TIME && isAdmin) {
              if (option?.value === ACCOUNTING_METHODS.ACCRUAL) {
                setFieldValue("payment_amount", 399);
              }

              if (option?.value === ACCOUNTING_METHODS.CASH) {
                setFieldValue("payment_amount", 199);
              }
            }
          };

          const onSeasonChange = (
            option: MultiValue<OptionData> | SingleValue<OptionData>
          ) => {
            if (option instanceof Array) {
              return;
            }

            if (!option?.value) {
              return;
            }

            const fromDate = dayjs()
              .year(Number(option.value))
              .startOf("year")
              .format(YYYY_MM_DD);

            const toDate = dayjs()
              .year(Number(option.value || 0))
              .endOf("year")
              .format(YYYY_MM_DD);

            if (has_from_date) {
              setFieldValue("from_date", fromDate);
            }

            if (has_to_date) {
              setFieldValue("to_date", toDate);
            }
          };

          const quarterData =
            Array.isArray(metadata) &&
            (metadata as QuarterlyAdvanceTaxMetadata)?.find(
              (item) => item.value === values.quarter
            );

          const selectedState =
            values.state_id && states
              ? states.find((s) => s.state_id === values.state_id)
              : undefined;

          const pricingTierOptions =
            selectedState?.priority_price != null
              ? pricingTierConfig.options
              : pricingTierConfig.options.filter(
                  (option) =>
                    option.value !== pricingTierConfig.priorityTierValue
                );

          const selectedStatePrice = getPriceForTier(
            selectedState,
            values.pricing_tier as PricingTier
          );

          let displayPrice: string | number | null | undefined = approx_price;
          if (selectedStatePrice != null) {
            displayPrice = selectedStatePrice;
          } else if (pricingTierConfig.fallbackPrice != null) {
            displayPrice = pricingTierConfig.fallbackPrice;
          }

          return (
            <>
              <Modal.Body>
                <Form className="t-m-0">
                  <div className="t-mb-4">
                    <DeletedCartItemInfo {...values} />
                  </div>
                  <div className="t-grid t-grid-cols-2 t-gap-4">
                    {!hideEntity && (
                      <ToolTip text="Change entity from top right of the page">
                        <div>
                          <Combobox
                            isDisabled
                            components={{
                              DropdownIndicator: () => null,
                            }}
                            menuPortalTarget={document.body}
                            name="entity_id"
                            withForm
                            label="Entity"
                            value={
                              selectedEntity && {
                                label: (
                                  <EntityLabel
                                    name={selectedEntity.name}
                                    countryCode={selectedEntity.country_code}
                                  />
                                ),
                                value: selectedEntity.uuid,
                              }
                            }
                            options={group.entities?.map((entity) => ({
                              label: (
                                <EntityLabel
                                  name={entity.name}
                                  countryCode={entity.country_code}
                                />
                              ),
                              value: entity.uuid,
                            }))}
                          />
                        </div>
                      </ToolTip>
                    )}

                    {allSeasons && allSeasons.length > 0 && (
                      <Combobox
                        required
                        menuPortalTarget={document.body}
                        name="season"
                        withForm
                        label="Season"
                        onChange={onSeasonChange}
                        value={
                          Boolean(values.season)
                            ? {
                                value: String(values.season),
                                label: values.season,
                              }
                            : null
                        }
                        options={allSeasons?.map((season: number) => ({
                          value: String(season),
                          label: season,
                        }))}
                      />
                    )}

                    {quarters && quarters.length > 0 && (
                      <Combobox
                        required
                        menuPortalTarget={document.body}
                        name="quarter"
                        withForm
                        label="Quarter"
                        value={
                          Boolean(values.quarter)
                            ? {
                                value: values.quarter,
                                label: values.quarter,
                              }
                            : null
                        }
                        options={quarters?.map((quarter: string) => ({
                          value: quarter,
                          label: quarter,
                        }))}
                      />
                    )}

                    {isAdhoc && (
                      <TextInput
                        name="task_name"
                        label="Task name"
                        placeholder="Enter task name"
                      />
                    )}
                    {has_from_date && (
                      <>
                        <Field name="from_date">
                          {({ field }: FieldProps) => {
                            return (
                              <DateInput
                                {...field}
                                label="Start from"
                                placeholder="Select month and year"
                                dateFormat="MMMM-yyyy"
                                showMonthYearPicker
                                required
                                portalId="startDate"
                                openToDate={new Date(selectedYear || "")}
                                maxDate={dayjs(selectedYear)
                                  .endOf("year")
                                  .toDate()}
                              />
                            );
                          }}
                        </Field>
                      </>
                    )}

                    {has_to_date && (
                      <Field name="to_date">
                        {({ field }: FieldProps) => {
                          return (
                            <DateInput
                              {...field}
                              label="To"
                              placeholder="Select month and year"
                              dateFormat="MMMM-yyyy"
                              showMonthYearPicker
                              required
                              portalId="startDate"
                              minDate={new Date(values.from_date)}
                              openToDate={new Date(selectedYear || "")}
                              maxDate={dayjs(selectedYear)
                                .endOf("year")
                                .toDate()}
                            />
                          );
                        }}
                      </Field>
                    )}

                    {has_accounting_method && (
                      <Combobox
                        required
                        withForm
                        label="Accounting method"
                        name="accounting_method"
                        options={ACCOUNTING_METHODS_OPTIONS}
                        onChange={onAccountingMethodChange}
                        value={
                          ACCOUNTING_METHODS_OPTIONS.find(
                            (am) => am.value === values.accounting_method
                          ) || null
                        }
                      />
                    )}

                    {states && !isCustomer && states.length > 0 && (
                      <Combobox
                        menuPortalTarget={document.body}
                        name="state_id"
                        withForm
                        label="State"
                        options={states?.map(({ state_id, name }) => ({
                          value: state_id,
                          label: name,
                        }))}
                        onChange={(option) => {
                          if (option instanceof Array || !option) return;
                          setFieldValue("state_id", option.value);
                          const newState = states.find(
                            (s) => s.state_id === option.value
                          );
                          const tier =
                            newState?.priority_price != null
                              ? values.pricing_tier
                              : pricingTierConfig.standardTierValue;
                          setFieldValue("pricing_tier", tier);
                          const paymentAmount = resolvePaymentAmountForTier(
                            newState,
                            tier as PricingTier
                          );
                          if (paymentAmount !== undefined) {
                            setFieldValue("payment_amount", paymentAmount);
                          }
                        }}
                      />
                    )}

                    {showPricingTier && (
                      <PricingTierSelector
                        pricingTier={values.pricing_tier}
                        pricingTierOptions={pricingTierOptions}
                        turnaround={pricingTierConfig.turnaround}
                        onChange={(tier) => {
                          setFieldValue("pricing_tier", tier);
                          const paymentAmount = resolvePaymentAmountForTier(
                            selectedState,
                            tier as PricingTier
                          );
                          if (paymentAmount !== undefined) {
                            setFieldValue("payment_amount", paymentAmount);
                          }
                        }}
                      />
                    )}

                    {quantity_type && (
                      <div className="t-col-span-2 t-flex t-flex-col t-gap-2">
                        <span className="t-text-body-sm t-text-text-30">
                          Number of {quantity_type}(s)
                        </span>
                        <NumericStepper name="quantity" />
                      </div>
                    )}
                    {(base_task_key === STATE_FILING ||
                      base_task_key === AD_HOC) && (
                      <DateField
                        name="deadline"
                        dateProps={{
                          label: "Deadline",
                          required: true,
                          onDateChange: (date: Date) =>
                            setFieldValue("deadline", date),
                          rightComponent: (
                            <CalendarIcon className="t-text-text-30 -t-mt-0.5" />
                          ),
                        }}
                      />
                    )}

                    {metadata?.hasOwnProperty("fields") &&
                      "fields" in metadata &&
                      metadata.fields[0]?.type !== undefined && (
                        <>
                          {metadata.fields.map((data) => {
                            if (data.type === "bundle_checkbox") {
                              return (
                                <div
                                  className="t-space-y-2 t-col-span-2"
                                  key={data.base_task_template_id}
                                >
                                  <div className="t-text-body-sm t-text-text-30">
                                    {data.title}
                                  </div>
                                  <Checkbox
                                    name="bundle"
                                    onChange={(e) =>
                                      setFieldValue("bundle", e.target.checked)
                                    }
                                    label={data.label}
                                  />
                                </div>
                              );
                            }
                            return null;
                          })}
                        </>
                      )}

                    {description && (
                      <div className="t-col-span-2">
                        <div className="t-text-body-sm t-text-text-30">
                          About
                        </div>
                        <div
                          className="t-text-body-sm t-text-text-100"
                          dangerouslySetInnerHTML={{
                            __html: description,
                          }}
                        />
                      </div>
                    )}
                    {isCustomDeadline && selectedProduct.metadata && (
                      <div>
                        <div className="t-text-body-sm t-text-text-30">
                          Typical Deadline
                        </div>
                        <AdvanceTaxDeadline
                          metadata={
                            selectedProduct.metadata as
                              | YearlyAdvanceTaxMetadata
                              | QuarterlyAdvanceTaxMetadata
                          }
                          isYearly={base_task_key === YEARLY_ADVANCE_TAX}
                          selectedQuarter={values.quarter}
                        />
                      </div>
                    )}
                    {!isCustomDeadline && approx_deadline && (
                      <div>
                        <div className="t-text-body-sm t-text-text-30">
                          Typical Deadline
                        </div>
                        <div className="t-text-subtext t-text-text-100">
                          {dayjs(approx_deadline).format("Do MMMM")}
                        </div>
                      </div>
                    )}

                    {(displayPrice && !isNaN(Number(displayPrice))) ||
                    isCustomer ? (
                      <div
                        className={classNames({
                          "t-col-span-2":
                            base_task_key === US_BOOKKEEPING_ONE_TIME,
                        })}
                      >
                        <div className="t-text-body-sm t-text-text-30 t-flex t-items-center t-gap-1">
                          Price
                          {(base_task_key === STATE_ANNUAL_REPORTS ||
                            isForeignQualification ||
                            isPayrollRegistration) && (
                            <ToolTip text="Government fees included in this amount">
                              <span className="t-text-text-30 t-flex">
                                <Info />
                              </span>
                            </ToolTip>
                          )}
                        </div>
                        {quarterData &&
                        base_task_key === QUARTERLY_ADVANCE_TAX ? (
                          <AdvanceQuarterlyTaxPrice quarterData={quarterData} />
                        ) : (
                          <>
                            {base_task_key === US_BOOKKEEPING_ONE_TIME ? (
                              <>
                                {currentSeason ? (
                                  <CalculatedAmountForAnnualCatchup
                                    currentSeason={currentSeason}
                                    calculatePrice={() =>
                                      calculateBookkeepingPrice({
                                        startDate: values.from_date,
                                        endDate: values.to_date,
                                        accountingMethod:
                                          values.accounting_method as ACCOUNTING_METHOD_TYPES,
                                        setPrice,
                                      })
                                    }
                                  />
                                ) : (
                                  <div>
                                    <div className="t-text-subtitle">
                                      Post-paid
                                    </div>
                                    <div className="t-flex t-gap-1 t-text-text-30 t-text-body-sm">
                                      <InfoSolid size="12" />
                                      <span>
                                        The price will be calculated based on
                                        the accounting method selected and no.of
                                        transactions during the service period.{" "}
                                        <Link
                                          to={`${INKLE_WEBSITE_URL}pricing#Inkle%20Books`}
                                        >
                                          Know more
                                        </Link>
                                      </span>
                                    </div>
                                  </div>
                                )}
                              </>
                            ) : (
                              <p className="t-text-subtitle">
                                <AmountSuperScript
                                  amount={Number(displayPrice || "0")}
                                />
                              </p>
                            )}
                          </>
                        )}
                      </div>
                    ) : (
                      <div className="t-col-span-2 t-flex t-gap-2 t-items-end">
                        <PriceInput
                          name="payment_amount"
                          label="Price"
                          type="number"
                          required
                        />
                        {base_task_key === US_BOOKKEEPING_ONE_TIME && (
                          <ConditionalToolTip
                            condition={
                              values.from_date &&
                              values.to_date &&
                              values.accounting_method
                                ? "Calculate price based on number of transactions"
                                : "Please select start date, end date and accounting method to calculate price"
                            }
                          >
                            <span>
                              <Button
                                disabled={
                                  !Boolean(
                                    values.from_date &&
                                      values.to_date &&
                                      values.accounting_method
                                  )
                                }
                                isLoading={gettingTransactions}
                                type="button"
                                onClick={() =>
                                  calculateBookkeepingPrice({
                                    startDate: values.from_date,
                                    endDate: values.to_date,
                                    accountingMethod:
                                      values.accounting_method as ACCOUNTING_METHOD_TYPES,
                                    setPrice,
                                  })
                                }
                              >
                                Calculate
                              </Button>
                            </span>
                          </ConditionalToolTip>
                        )}
                      </div>
                    )}
                  </div>
                </Form>
              </Modal.Body>

              <Modal.Footer>
                <div
                  className={classNames("t-flex", {
                    "t-justify-between": Boolean(setCurrentStep),
                    "t-justify-end": !Boolean(setCurrentStep),
                  })}
                >
                  {setCurrentStep && (
                    <Button onClick={() => setCurrentStep(2)}>Back</Button>
                  )}
                  <Button
                    customType="primary"
                    onClick={submitForm}
                    isLoading={addingProductToCart}
                    disabled={addingProductToCart}
                  >
                    <div className="t-flex t-items-center t-gap-1.5">
                      {!submitBtnText && <Cart />}
                      <span>{submitBtnText || "Add to Cart"}</span>
                    </div>
                  </Button>
                </div>
              </Modal.Footer>
            </>
          );
        }}
      </Formik>
    </ConditionalAddProductToCartWrapper>
  );
};
